import HawkCatcher, { HawkJavaScriptEvent } from '@hawk.so/javascript';

import { TMonitoringContext, TMonitoringOptions, TMonitoringIgnoreOption } from './types';

class Monitoring {
  private hawk: HawkCatcher | null = null;

  private release: string | undefined;

  private options: Omit<TMonitoringOptions, 'ignoreErrors' | 'isEnabled'> | null = null;

  private isEnabled = false;

  private ignoreErrors: TMonitoringIgnoreOption = [];

  private ignoreSources: TMonitoringIgnoreOption = [];

  constructor(options: TMonitoringOptions) {
    const {
      release,
      isEnabled = true,
      ignoreErrors = [],
      ignoreSources = [],
      getExtraContext,
      ...restOptions
    } = options;

    this.release = release;
    this.options = restOptions;
    this.isEnabled = isEnabled;
    this.ignoreErrors = ignoreErrors;
    this.ignoreSources = ignoreSources;
    this.getExtraContext = getExtraContext || (() => ({}));
  }

  init(context?: TMonitoringContext) {
    if (!this.options || !this.isEnabled) {
      return;
    }

    this.hawk = new HawkCatcher({
      ...this.options,
      release: this.release,
      consoleTracking: true,
      disableGlobalErrorsHandling: false,
      beforeSend: (event) => this.beforeSend(event),
      context: {
        ...this.options.context,
        ...context,
      },
    });
  }

  test() {
    this.hawk?.test();
  }

  catchError(error: Error) {
    if (!this.hawk) {
      return;
    }

    this.hawk.captureError(error);
  }

  sendMessage(error: Error, data?: Record<string, unknown>) {
    if (!this.hawk) {
      return;
    }

    this.hawk.send(error, data as Parameters<HawkCatcher['send']>[1]);
  }

  getExtraContext(): TMonitoringContext {
    return {};
  }

  private beforeSend(event: HawkJavaScriptEvent) {
    const extraContext = this.getExtraContext();

    event.context = { ...event.context, ...extraContext };

    if (this.options?.beforeSend) {
      return this.options.beforeSend(event);
    }

    if (
      // @ts-expect-error Некорректные типы в библиотеке, хотя в исходном коде принимаются все значения
      this.shouldIgnoreError(new Error(event.title)) ||
      this.shouldIgnoreSource(event.backtrace)
    ) {
      return false;
    }

    return event;
  }

  private shouldIgnoreError(error: Error) {
    const message = error instanceof Error ? error.message : String(error);

    return this.ignoreErrors.some((pattern) =>
      typeof pattern === 'string' ? message.includes(pattern) : pattern.test(message),
    );
  }

  private shouldIgnoreSource(backtrace: HawkJavaScriptEvent['backtrace']) {
    if (!backtrace) {
      return false;
    }

    return backtrace.some((frame) =>
      this.ignoreSources.some((pattern) =>
        typeof pattern === 'string'
          ? frame.file.includes(pattern) || frame.function?.includes(pattern)
          : pattern.test(frame.file) || (frame.function && pattern.test(frame.function)),
      ),
    );
  }
}

export default Monitoring;
